/**
 * 关卡大 boss 战前对话（Touhou 式立绘对话）。
 *
 * 数据结构：{ [stageKey]: [{who, ...}] }，stageKey 为 1..6 与 'extra'。
 * 行类型：
 *   { who: 'boss', text: '…' }                        boss 固定台词（沿用符卡自述的声口）
 *   { who: 'boss', text: '…', vs: { charId: '…' } }   可选 vs：对特定自机的差别反应
 *   { who: 'player', by: { charId: '…' }, default: '…' } 自机台词，按 8 角色性格差异化
 * 展示时经 L() 走 CONTENT 表多语（改后必跑 tools/i18n-check.mjs）。
 * 消费方：gameScene（mode='dialogue'）。
 * node 可直接 import。
 */

/** 取一行的实际文本：boss 行看 vs 覆盖，player 行看角色专属，最后 default/text。 */
export function dialogueText(line, charId) {
  if (line.who === 'boss') {
    if (line.vs && line.vs[charId]) return line.vs[charId];
    return line.text;
  }
  return (line.by && line.by[charId]) || line.default;
}

export const STAGE_DIALOGUE = {
  1: [
    { who: 'boss', text: '何人在寡人的云海踏浪？风都替你紧张起来了。' },
    {
      who: 'player',
      by: {
        happy: '我叫开开心！借你的云打个招呼——弹幕，来啦！',
        cool: '过路。让开。',
        angry: '挡路的？正好，我手痒了！',
        cry: '那、那个……我只是路过……呜……',
        confused: '咦？云……成精了？',
        cheer: '路过顺便给你加个油——然后打一架！',
        sleepy: '唔……好软的云……能睡个午觉吗……',
        moe: '萌萌路过~大个子你会比心吗？',
      },
      default: '只是路过的弹幕使。借过。',
    },
    { who: 'boss', text: '借过？云海没有路，只有风向。逆着寡人的风，半步也休想。' },
    {
      who: 'player',
      by: {
        happy: '那我就顺着你的风跳个舞给你看！',
        cool: '风会停。你不会赢。',
        angry: '我把你的风全打回去！',
        cry: '对不起对不起……但、但是我不能退……！',
        confused: '风……往哪边吹来着？',
        cheer: '风向什么的，看我的气势把它吹回去！',
        sleepy: '逆风……好困……快点打完吧……',
        moe: '风再大，也吹不走我的可爱~',
      },
      default: '那我只好自己开一条路了。',
    },
    { who: 'boss', text: '好胆色。那就让积雨云看看——你能接住几滴天罚。' },
  ],
  2: [
    { who: 'boss', text: '水面上的客人……是灯的甜味引你下来的吗？' },
    {
      who: 'player',
      by: {
        happy: '是你的派对灯光太好看啦！带我一个！',
        cool: '你的灯，太晃眼。',
        angry: '少废话！上来就打！',
        cry: '呜……这里好黑……你的灯可以借我一下吗……',
        confused: '咦，是你约我下来的吗？',
        cheer: '深海巡游第一站！克拉肯，接招！',
        sleepy: '水里好舒服……想边睡边打……',
        moe: '哇，八条腿！可以帮我扎头发吗？',
      },
      default: '我只是追着弹幕的轨迹下来的。',
    },
    { who: 'boss', text: '深海没有甜，只有压。你的骨头会先替我尝到。' },
    {
      who: 'player',
      by: {
        happy: '压强什么的，笑着就顶住啦！',
        cool: '我的枪比你的压更重。',
        angry: '看我把你煮成章鱼烧！',
        cry: '骨、骨头会断掉吗……好可怕……但我不走！',
        confused: '压？是哪一种压？',
        cheer: '我的热血，深海也煮得沸！',
        sleepy: '压一压……更好睡……',
        moe: '压力讨厌~萌萌要浮起来！',
      },
      default: '那就比比谁先被压垮。',
    },
    { who: 'boss', text: '有趣。腕足已张开——这场拥抱，你躲不掉。' },
  ],
  3: [
    { who: 'boss', text: '警告：核心区域，非授权单位。立即停机，接受熔解。' },
    {
      who: 'player',
      by: {
        happy: '拒绝停机！我还要去玩呢！',
        cool: '授权？我就是。',
        angry: '熔解？！我先把你拆了卖废铁！',
        cry: '对、对不起，我这就走……咦，走不掉？',
        confused: '停机……是按哪个钮？',
        cheer: '检测报告：你这炉子火力不足！',
        sleepy: '停机……好耶……那我可以睡了吗……',
        moe: '熔解好可怕~萌萌会化掉的！',
      },
      default: '我只是借道的，不打算停机。',
    },
    { who: 'boss', text: '借道申请驳回。本炉每分钟熔解借道单位十二个。' },
    {
      who: 'player',
      by: {
        happy: '那我做第十三个——特别难熔的那种！',
        cool: '你的产量，今天归零。',
        angry: '才十二个？我一个顶一百个！',
        cry: '呜……我不想被熔解……我要回家……',
        confused: '十二个……那第十三个免费吗？',
        cheer: '熔解之前，先尝尝我的热血！',
        sleepy: '十二个……数着数着就困了……',
        moe: '驳回无效~萌萌的票是永久的！',
      },
      default: '那今天这个数字到你为止。',
    },
    { who: 'boss', text: '炉心升温。链齿咬合——开始熔解程序。' },
  ],
  4: [
    { who: 'boss', text: '慢些。再慢些。你听，腐叶下面，都是千年的根。' },
    {
      who: 'player',
      by: {
        happy: '树爷爷好！你的叶子我能捡一片吗？',
        cool: '让路，老树。',
        angry: '千年？烧掉只要十分钟！',
        cry: '对、对不起……我踩疼你的根了吗……',
        confused: '根……在跟我说话？',
        cheer: '千年古树！值得我全力一战！',
        sleepy: '千年……那你一定很困吧……',
        moe: '树爷爷，萌萌可以在这里野餐吗？',
      },
      default: '久仰。我只是路过的客人。',
    },
    { who: 'boss', text: '客人踩断了三根细根。千年了，我学会的第一课，就是不再原谅。' },
    {
      who: 'player',
      by: {
        happy: '那我赔你一场热闹！森林最喜欢热闹了！',
        cool: '我数着的。两根。',
        angry: '才三根？我让你断三千根！',
        cry: '呜哇——对不起！我、我给你浇水！',
        confused: '三根……是左边那三根吗？',
        cheer: '那我用全力道歉——全力一战！',
        sleepy: '原谅我嘛……我只是想睡觉……',
        moe: '萌萌这么轻，怎么会断呢~？',
      },
      default: '那我只好用胜利道歉了。',
    },
    { who: 'boss', text: '藤蔓不急。它有一整个下午，把你留在这里。' },
  ],
  5: [
    { who: 'boss', text: '你看得见我吗？这里很暗。暗到只剩我在看你。' },
    {
      who: 'player',
      by: {
        happy: '看得见呀！这么大一只眼睛，想看不见都难！',
        cool: '看够了就闭眼。',
        angry: '看什么看！把你打成瞎子！',
        cry: '呜……被盯着看……好可怕……',
        confused: '咦，是谁在说话？',
        cheer: '那就看看我的光！亮瞎你！',
        sleepy: '黑黑的……正好睡觉……',
        moe: '萌萌这么可爱，看个够吧~',
      },
      default: '彼此彼此。',
    },
    { who: 'boss', text: '每个进来的人都这么说。他们最后都把眼睛留在了这里。' },
    {
      who: 'player',
      by: {
        happy: '眼睛才不给你！我还要用它看明天的太阳！',
        cool: '试试。',
        angry: '我把你的眼睛挖出来陪葬！',
        cry: '不、不要！我的眼睛还要留着哭……',
        confused: '留眼睛……怎么留？',
        cheer: '我的视线是燃着的——拿不住！',
        sleepy: '眼睛给你……眼皮给我留下……',
        moe: '不给~萌萌要眼睛来卖萌的！',
      },
      default: '你留不住我的。',
    },
    { who: 'boss', text: '那现在，换我仔细看看你——别眨眼。' },
  ],
  6: [
    { who: 'boss', text: '旅人，你走到了星屑的尽头。再往前，就是终焉。' },
    {
      who: 'player',
      by: {
        happy: '终焉？听起来像最后一关！那我更要冲了！',
        cool: '终焉见多了。让开。',
        angry: '终焉？！我先把你终焉了！',
        cry: '终、终焉……听起来好可怕……但我不能回头……',
        confused: '咦？这里就是最后一关吗？',
        cheer: '燃烧到星屑的尽头——正合我意！',
        sleepy: '终焉……打完就能睡了吧……',
        moe: '终焉是什么？能吃吗？',
      },
      default: '那就把终焉变成我的终点线。',
    },
    { who: 'boss', text: '星尘核心的每一次脉动，都是一个世界的余烬。你要以肉身贴近它？' },
    {
      who: 'player',
      by: {
        happy: '余烬也是光呀！我来替你点亮下一把火！',
        cool: '我贴近过的核心，不止你一个。',
        angry: '脉动？打爆了就不脉了！',
        cry: '余、余烬会烫吗……呜……但我不会退！',
        confused: '贴近？多远算贴近？',
        cheer: '我的血也是余烬燃的！比比谁烫！',
        sleepy: '余烬……暖烘烘的……适合睡觉……',
        moe: '余烬也要萌萌地烧！',
      },
      default: '贴近了，才能打穿它。',
    },
    { who: 'boss', text: '那么——银河回旋，为你让路。前提是，你过得去。' },
  ],
  extra: [
    { who: 'boss', text: '回廊的考官见过许多来客。走到王座前的，你是第一个。' },
    {
      who: 'player',
      by: {
        happy: '第一个？那我要拿满分！',
        cool: '考官？我只带枪来。',
        angry: '少摆架子！你的题我全拆了！',
        cry: '考、考试？我没复习……呜……',
        confused: '咦？要考试吗？考什么？',
        cheer: '毕业考是吧！看我的热血答卷！',
        sleepy: '考完……就能睡了吧……',
        moe: '考官老师~可以开卷吗？',
      },
      default: '第一个，但不会是最后一个。',
    },
    { who: 'boss', text: '十道符卡，没有续关。答错一题，就留在回廊里当星屑。' },
    {
      who: 'player',
      by: {
        happy: '十道全对！星屑还是留给你自己吧！',
        cool: '出题吧。',
        angry: '一题都不会错！错了我把回廊烧了！',
        cry: '不、不要当星屑……我要回家……',
        confused: '星屑……和星星是亲戚吗？',
        cheer: '十道而已！我的热血够答一百道！',
        sleepy: '十道……数着数着就困了……',
        moe: '萌萌的答案都是满分答案~',
      },
      default: '那就一题一题打过去。',
    },
    { who: 'boss', text: '很好。终焉回廊，开考。' },
  ],
};
